import React from 'react';
import { useSearchParams } from 'react-router-dom';

import Link from '@dashboard/components/mui-atoms/Link';
import Header from '@dashboard/components/pages/QueryListPage/PageWrapper/Header';
import { createTableComponent } from '@dashboard/components/pages/QueryListPage/TableList/WpDataGrid';
import { type FPlatformFragment, usePlatformQuery, usePlatformsQuery } from '@dashboard/generated/wp/administrator';
import { useCurrentPage } from '@dashboard/hooks/route/useCurrentPage';
import { MenuItem, Paper, Select, type SelectProps, Stack, Typography } from '@mui/material';
import useQueryDataTableProps from '@wp-react/hooks/useQueryDataTableProps';

import { DateFormatter } from '@waypoint/react-formatters';

const PlatformsTable = createTableComponent<FPlatformFragment>({
  emptyResults: <>No platforms found</>,
  columns: [
    {
      headerName: 'Actions',
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" spacing={1}>
            <Link href={`/admin/o/${row.id}`}>Admin</Link>
            <Link href={`/o/${row.id}`}>View</Link>
          </Stack>
        );
      },
    },
    { headerName: 'ID', renderCell: ({ row }) => <Typography>{row.id}</Typography> },
    { headerName: 'Name', renderCell: ({ row }) => <Typography>{row.name}</Typography> },
    {
      headerName: 'Created at',
      renderCell: ({ row }) => (
        <Typography>
          <DateFormatter value={row.createdAt} />
        </Typography>
      ),
    },
    { headerName: 'Billing email', renderCell: ({ row }) => <Typography>{row.billingEmailAddress}</Typography> },
    {
      headerName: 'Sending status',
      renderCell: function SendingStatus({ row }) {
        const { data } = usePlatformQuery({ variables: { id: row.id } });
        const value = data?.Platform.sendingStatus;
        return !value ? null : <Typography>{value}</Typography>;
      },
    },
    { headerName: 'Tier', renderCell: ({ row }) => <Typography>{row.activeWaypointTier}</Typography> },
    {
      headerName: 'Email messages',
      renderCell: function EmailMessagesCount({ row }) {
        const { data } = usePlatformQuery({ variables: { id: row.id } });
        const value = data?.Platform.emailMessagesCount;
        return !value ? null : <Typography>{value}</Typography>;
      },
    },
    {
      headerName: 'Workflow runs',
      renderCell: function WorkflowRunsCount({ row }) {
        const { data } = usePlatformQuery({ variables: { id: row.id } });
        const value = data?.Platform.workflowRunsCount;
        return !value ? null : <Typography>{value}</Typography>;
      },
    },
  ],
});

export default function AdministrationPage() {
  const currentPage = useCurrentPage();
  const [params, setQueryParams] = useSearchParams();
  const tier = params.get('tier');

  const { data } = usePlatformsQuery({ variables: { tier, page: currentPage } });
  const { pagination } = useQueryDataTableProps(data?.Platforms);
  const nodes = data?.Platforms.nodes;

  const onTierChange: SelectProps['onChange'] = (ev) => {
    setQueryParams((prevParams) => {
      const value = ev.target.value;
      if (value === 'LIVE') {
        prevParams.delete('tier');
      } else if (typeof value !== 'string') {
        prevParams.delete('tier');
      } else if (value.trim().length === 0) {
        prevParams.delete('tier');
      } else {
        prevParams.set('tier', value);
      }
      return prevParams;
    });
  };

  return (
    <Paper>
      <Header
        title="Platforms"
        pagination={pagination}
        actions={
          <Select onChange={onTierChange} value={tier || 'LIVE'} size="small">
            <MenuItem value="LIVE">LIVE</MenuItem>
            <MenuItem value="SAMPLE">SAMPLE</MenuItem>
            <MenuItem value="SANDBOX">SANDBOX</MenuItem>
            <MenuItem value="FREE">FREE</MenuItem>
            <MenuItem value="STARTER">STARTER</MenuItem>
            <MenuItem value="GROWTH">GROWTH</MenuItem>
            <MenuItem value="PRO">PRO</MenuItem>
            <MenuItem value="UNLIMITED">UNLIMITED</MenuItem>
          </Select>
        }
      />
      <PlatformsTable nodes={nodes} />
    </Paper>
  );
}
